import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import './MapCard.css';

import LightIndicator from '../LightIndicator/LightIndicator';
import Button from '../common/Button/Button';

const MapCard = ({
	primaryId,
	primaryName,
	isActive,
	isBroadcasting,
	bitrate,
	duration,
	batteryLevel,
	isCharging,
	batteryTemperature,
	signalStrength, // Keep for compatibility if needed elsewhere
	devices = [],
	focusedPrimaryId,
	isChangingStartStopInProgress,
	screenSize,
	handlePrimaryFocus,
	clickStartStop,
	locatePrimary
}) => {
	const isFocused = focusedPrimaryId === primaryId;
	const [isExpanded, setIsExpanded] = useState(isFocused);

	useEffect(() => {
		setIsExpanded(isFocused);
	}, [isFocused]);

	const renderSignalGauge = (device) => {
		const rawStrength = device.signalStrength;
		const strength = (rawStrength !== null && rawStrength !== undefined) ? parseFloat(rawStrength) : NaN;

		let color = 'rgba(255, 255, 255, 0.1)';
		if (!isNaN(strength)) {
			if (strength < 30) color = '#e74c3c';
			else if (strength < 60) color = '#f39c12';
			else color = '#4cd137';
		}

		const providerLabel = device.PROVIDER || device.provider || device.providerName || device.networkType || device.type || 'Cellular';
		const lowerProvider = providerLabel.toLowerCase();

		let icon = 'signal_cellular_alt';
		if (lowerProvider.includes('wifi') || lowerProvider.includes('wi-fi')) icon = 'wifi';
		else if (lowerProvider.includes('ethernet')) icon = 'lan';

		const displayLabel = providerLabel.length > 20 ? providerLabel.substring(0, 18) + '..' : providerLabel;

		return (
			<div className="connection-gauge-row" key={device.id || device.name}>
				<div className="gauge-label-group">
					<i className="material-icons gauge-icon" style={{ color: !isNaN(strength) ? color : 'rgba(255,255,255,0.2)' }}>{icon}</i>
					<span className="gauge-name">{displayLabel}</span>
				</div>
				<div className="gauge-bar-container">
					<div
						className="gauge-bar-fill"
						style={{
							width: !isNaN(strength) ? `${Math.round(strength)}%` : '0%',
							backgroundColor: color,
							boxShadow: !isNaN(strength) ? `0 0 10px ${color}44` : 'none'
						}}
					/>
				</div>
				<span className="gauge-percent" style={{ color: !isNaN(strength) ? color : 'rgba(255,255,255,0.3)' }}>
					{isNaN(strength) ? 'N/A' : `${Math.round(strength)}%`}
				</span>
			</div>
		);
	};

	const renderSignalItem = (device) => {
		const rawStrength = device.signalStrength;
		const strength = (rawStrength !== null && rawStrength !== undefined) ? parseFloat(rawStrength) : NaN;

		let color = 'rgba(255, 255, 255, 0.2)'; // Default gray
		if (!isNaN(strength)) {
			if (strength < 30) color = '#e74c3c'; // Red
			else if (strength < 60) color = '#f39c12'; // Orange
			else color = '#4cd137'; // Green
		}

		// Use Provider metadata (WiFi, Carrier name) - checking both case options
		const providerLabel = device.PROVIDER || device.provider || device.providerName || device.networkType || device.type || 'Cellular';
		const lowerProvider = providerLabel.toLowerCase();

		let icon = 'signal_cellular_alt';
		if (lowerProvider.includes('wifi') || lowerProvider.includes('wi-fi')) icon = 'wifi';
		else if (lowerProvider.includes('ethernet')) icon = 'lan';

		// Show carrier name (e.g., 'WiFi', 'Orange', 'Vodafone')
		const displayLabel = providerLabel;

		return (
			<div className="mini-signal-item" key={device.id || device.name}>
				<i className="material-icons mini-signal-icon" style={{ color: !isNaN(strength) ? color : 'rgba(255,255,255,0.4)' }}>{icon}</i>
				<span className="mini-battery-text" style={{ color: !isNaN(strength) ? color : 'rgba(255,255,255,0.7)' }}>
					{isNaN(strength) ? 'N/A' : `${Math.round(strength)}%`}
				</span>
				<span className="mini-type-label">({displayLabel})</span>
			</div>
		);
	};

	const renderBattery = (level, charging) => {
		const batLevel = parseFloat(level);
		const isOffline = isNaN(batLevel);

		let color = '#4cd137'; // Green
		if (!isOffline) {
			if (batLevel < 20) color = '#e74c3c'; // Red
			else if (batLevel < 40) color = '#f39c12'; // Orange
		} else {
			color = 'rgba(255, 255, 255, 0.1)';
		}

		return (
			<div className="mini-battery-wrapper">
				<div className="mini-battery-container">
					<div className={`mini-battery-outline ${isOffline ? 'offline-outline' : ''}`}>
						<div
							className="mini-battery-fill"
							style={{ width: `${isOffline ? 0 : Math.min(100, batLevel)}%`, backgroundColor: color }}
						/>
						{charging && !isOffline && <i className="material-icons mini-charging-bolt">flash_on</i>}
					</div>
					<div className={`mini-battery-cap ${isOffline ? 'offline-cap' : ''}`} />
				</div>
				<span className="mini-battery-text">{isOffline ? 'N/A' : `${Math.round(batLevel)}%`}</span>
			</div>
		);
	};

	return (
		<div
			className={`map-card shadow-lg ${isFocused ? 'selected' : ''}`}
			onClick={() => {
				if (!isFocused) {
					handlePrimaryFocus(primaryId);
				} else {
					setIsExpanded(!isExpanded);
				}
			}}
		>
			<div className="card-header bg-info-border">
				<a
					className={`card-link ${!isFocused ? 'collapsed' : ''}`}
					data-toggle="collapse"
					href={`#${primaryId}`}
				>
					<div className="map-card-header-main">
						<div className="map-card-header-left">
							<div className="primary-unit-name">
								{primaryName}
							</div>
							<div className="primary-unit-health">
								{renderBattery(batteryLevel, isCharging)}
								<div className="mini-signals-scroll-container">
									<div className="mini-signals-row">
										{(devices.length > 0) ? (
											devices.map(renderSignalItem)
										) : (
											// Placeholder for offline
											<div className="mini-signal-item">
												<i className="material-icons mini-signal-icon" style={{ color: 'rgba(255,255,255,0.1)' }}>signal_cellular_off</i>
												<span className="mini-battery-text" style={{ color: 'rgba(255,255,255,0.2)' }}>N/A</span>
											</div>
										)}
									</div>
									{devices.length >= 3 && (
										<div className="signals-overflow-indicator">
											<i className="material-icons overflow-dots-icon">more_horiz</i>
										</div>
									)}
								</div>
							</div>
						</div>
						<div className="map-card-header-right">
							<LightIndicator
								type="Primary"
								name={primaryName}
								isActive={isActive}
								isBroadcasting={isBroadcasting}
								componentLocation="map"
								screenSize={screenSize}
							/>
						</div>
					</div>
				</a>
			</div>

			<div
				id={primaryId}
				className={`collapse ${isExpanded ? 'show' : ''}`}
				data-parent="#map-accordion"
			>
				<div className="card-body px-3 card-map-color">
					<div className="metrics-grid mb-3">
						<div className="metric-item">
							<span className="metric-label">BITRATE</span>
							<span className="metric-value">{bitrate} <small>Mbps</small></span>
						</div>
						<div className="metric-item">
							<span className="metric-label">DURATION</span>
							<span className="metric-value">{duration}</span>
						</div>
						<div className="metric-item">
							<span className="metric-label">BATTERY</span>
							<span className="metric-value d-flex align-items-center">
								{batteryLevel !== undefined ? (
									<div className="mini-battery-wrapper">
										<div className="mini-battery-container">
											<div className={`mini-battery-outline ${!isActive ? 'offline-outline' : ''}`}>
												<div
													className={`mini-battery-fill ${!isActive ? 'offline-fill' : ''}`}
													style={{
														width: `${batteryLevel}%`,
														backgroundColor: batteryLevel >= 40 ? '#4cd137' : (batteryLevel >= 20 ? '#f39c12' : '#e74c3c')
													}}
												/>
											</div>
											<div className={`mini-battery-cap ${!isActive ? 'offline-cap' : ''}`} />
										</div>
										{isCharging && <i className="material-icons charging-icon">flash_on</i>}
										<span className="mini-battery-text">{batteryLevel}%</span>
									</div>
								) : 'N/A'}
							</span>
						</div>
						<div className="metric-item">
							<span className="metric-label">TEMPERATURE</span>
							<span className="metric-value">
								{batteryTemperature !== undefined ? (
									<span className={`temp-pill ${batteryTemperature > 41 ? 'temp-critical' :
										(batteryTemperature > 37 ? 'temp-warn' : 'temp-cool')
										}`}>
										{batteryTemperature}°C
									</span>
								) : 'N/A'}
							</span>
						</div>
						<div className="metric-item full-width mt-2">
							<span className="metric-label">NETWORK CONNECTIONS</span>
							<div className="connection-gauges-list mt-2">
								{devices.length > 0 ? (
									devices.map(d => renderSignalGauge(d))
								) : (
									<div className="no-signals-text">No active signals</div>
								)}
							</div>
						</div>
					</div>

					<div className="d-flex justify-content-between align-items-center">
						<Button
							iconName="location_searching"
							isDisabled={!isActive}
							clickHandler={(e) => {
								e.stopPropagation();
								locatePrimary();
							}}
						/>

						<button
							className={`btn btn-sm btn-action ${isBroadcasting ? 'btn-stop' : 'btn-start'}`}
							disabled={!isActive || isChangingStartStopInProgress}
							onClick={(e) => {
								e.stopPropagation();
								clickStartStop();
							}}
						>
							{isChangingStartStopInProgress && isFocused ? (
								<span className="spinner-border spinner-border-sm mr-1"></span>
							) : (
								<i className="material-icons mr-1">
									{isBroadcasting ? 'stop' : 'play_arrow'}
								</i>
							)}
							{isBroadcasting ? 'STOP' : 'START'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

MapCard.propTypes = {
	primaryId: PropTypes.number.isRequired,
	primaryName: PropTypes.string.isRequired,
	isActive: PropTypes.bool.isRequired,
	isBroadcasting: PropTypes.bool.isRequired,
	bitrate: PropTypes.string,
	duration: PropTypes.string,
	batteryLevel: PropTypes.number,
	isCharging: PropTypes.bool,
	signalStrength: PropTypes.number,
	focusedPrimaryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	isChangingStartStopInProgress: PropTypes.bool,
	screenSize: PropTypes.string,
	handlePrimaryFocus: PropTypes.func.isRequired,
	clickStartStop: PropTypes.func.isRequired,
	locatePrimary: PropTypes.func.isRequired
};

export default React.memo(MapCard);
